import React, { memo, useCallback, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import Lottie from 'lottie-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, BookOpen, Globe, Cpu, DollarSign, Trophy } from 'lucide-react'; // Added new icons
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom'; // Added for navigation

// Replace with actual asset imports
import octopusAnimation from '../assets/Octopus.json';
import aiSchoolImg from '../assets/aischool.jpg';
import footerImage from '../assets/Footer.png';
import harvardImg from '../assets/harvard.png';
import oxfordImg from '../assets/oxford.png';
import gameImg from '../assets/Game.jpg'; // Game.jpg import
import scholarshipImg from '../assets/Scholarship.jpg'; // Scholarship.jpg import

// Set Axios base URL
axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// TypeScript interfaces
interface Translation {
  header: { logo: string; contact: string };
  main: { title: string; subtitle: string; trustBadge: string };
  whatIs: { title: string; text: string };
  whyCollaborate: {
    title: string;
    feature1: { title: string; text: string };
    feature2: { title: string; text: string };
    feature3: { title: string; text: string };
    feature4: { title: string; text: string };
    feature5: { title: string; text: string };
  };
  cta: { title: string; text: string; button: string };
  footer: { tagline: string };
}

interface School {
  schoolCode: string | null;
  schoolName: string;
  city: string;
  pincode: string;
  schoolImage?: string;
  _id: string;
}

interface Student {
  _id: string;
  fullName: string;
  points: number;
}

// Translations for landing section
const translations: { en: Translation } = {
  en: {
    header: { logo: 'drOcto', contact: 'Join Now' },
    main: {
      title: 'Transform Your School with drOcto’s AI-Powered Learning',
      subtitle: 'Empower your students, enhance your curriculum, and position your school as a leader in innovative education with drOcto.',
      trustBadge: 'Designed in IIT Madras',
    },
    whatIs: {
      title: 'What is drocto?',
      text: 'drOcto is an AI-powered learning platform that interacts with students in real-time, evaluates their IQ and emotional intelligence, and generates personalized performance reports.',
    },
    whyCollaborate: {
      title: 'Why Collaborate with drOcto?',
      feature1: { title: 'Integrate in Classrooms/Labs', text: 'Part of AI-readiness or co-curricular programs.' },
      feature2: { title: 'More than Syllabus', text: 'Teach real-world skills to your students.' },
      feature3: { title: 'Adapt to LLM Technology', text: 'Teach how to effectively use the future AI tools.' },
      feature4: { title: 'Diversify Income', text: 'Earn 40% of the profit on each student.' },
      feature5: { title: 'Compete in Tournaments', text: 'With the best schools from India and the world.' },
    },
    cta: {
      title: 'Ready to Revolutionize Education?',
      text: 'Join drOcto today and lead the future of learning.',
      button: 'Get Started',
    },
    footer: {
      tagline: 'Empowering the Future of Education',
    },
  },
};

// Animation variants for landing section
const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

// Landing section sub-components
const TrustBadges = memo(() => (
  <motion.div
    className="flex justify-center gap-4 mt-6"
    initial="hidden"
    animate="visible"
    variants={fadeInUp}
  >
    <span className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-semibold shadow-md">
      {translations.en.main.trustBadge}
    </span>
  </motion.div>
));

TrustBadges.displayName = 'TrustBadges';

const TrainedOnSection = memo(() => (
  <motion.section
    className="bg-white/80 backdrop-blur-lg p-6 rounded-2xl shadow-lg text-center"
    initial="hidden"
    animate="visible"
    variants={fadeInUp}
  >
    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Built on World-Class Research</h2>
    <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-6" variants={staggerContainer}>
      {[
        { src: harvardImg, alt: 'Harvard University' },
        { src: oxfordImg, alt: 'Oxford University' },
      ].map((item, index) => (
        <motion.div
          key={index}
          className="flex justify-center"
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.3 }}
          variants={fadeInUp}
        >
          <img
            src={item.src}
            alt={item.alt}
            loading="lazy"
            className="w-24 h-24 object-contain rounded-lg"
          />
        </motion.div>
      ))}
    </motion.div>
  </motion.section>
));

TrainedOnSection.displayName = 'TrainedOnSection';

const WhatIsDrOcto = memo(() => (
  <motion.section
    className="bg-white/80 backdrop-blur-lg p-6 rounded-2xl shadow-lg text-center"
    initial="hidden"
    animate="visible"
    variants={fadeInUp}
  >
    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">{translations.en.whatIs.title}</h2>
    <p className="text-base md:text-lg text-gray-600 mb-6 max-w-2xl mx-auto">{translations.en.whatIs.text}</p>
    <motion.img
      src={aiSchoolImg}
      alt="drOcto in Classroom"
      loading="lazy"
      className="w-full max-w-md mx-auto rounded-lg shadow-md"
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.3 }}
    />
  </motion.section>
));

WhatIsDrOcto.displayName = 'WhatIsDrOcto';

const WhyCollaborate = memo(() => (
  <motion.section
    className="bg-white/80 backdrop-blur-lg p-6 rounded-2xl shadow-lg text-center"
    initial="hidden"
    animate="visible"
    variants={fadeInUp}
  >
    <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">{translations.en.whyCollaborate.title}</h2>
    <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" variants={staggerContainer}>
      {Object.values(translations.en.whyCollaborate)
        .slice(1)
        .map((item, index) => (
          <motion.div
            key={index}
            className="p-6 rounded-lg bg-white shadow-md hover:shadow-xl transition-shadow"
            whileHover={{ y: -10 }}
            transition={{ duration: 0.3 }}
            variants={fadeInUp}
          >
            {/* Add icon based on index */}
            {index === 0 && <BookOpen className="h-8 w-8 text-blue-600 mx-auto mb-3" />}
            {index === 1 && <Globe className="h-8 w-8 text-blue-600 mx-auto mb-3" />}
            {index === 2 && <Cpu className="h-8 w-8 text-blue-600 mx-auto mb-3" />}
            {index === 3 && <DollarSign className="h-8 w-8 text-blue-600 mx-auto mb-3" />}
            {index === 4 && <Trophy className="h-8 w-8 text-blue-600 mx-auto mb-3" />}
            <h3 className="text-lg md:text-xl font-semibold text-blue-600 mb-2">{item.title}</h3>
            <p className="text-sm md:text-base text-gray-600">{item.text}</p>
          </motion.div>
        ))}
    </motion.div>
  </motion.section>
));

WhyCollaborate.displayName = 'WhyCollaborate';

// New GameSection component
const GameSection = memo(() => {
  const navigate = useNavigate();
  return (
    <motion.section
      className="bg-white/80 backdrop-blur-lg p-6 rounded-2xl shadow-lg text-center"
      initial="hidden"
      animate="visible"
      variants={fadeInUp}
    >
      <motion.img
        src={gameImg}
        alt="drOcto Game"
        loading="lazy"
        className="w-full max-w-md mx-auto rounded-lg shadow-md mb-6"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
      />
      <motion.button
        className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold text-base shadow-md hover:bg-blue-700"
        onClick={() => navigate('/schoolwars')}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Champion Schools"
      >
        Champion Schools
      </motion.button>
    </motion.section>
  );
});

GameSection.displayName = 'GameSection';

// New ScholarshipSection component
const ScholarshipSection = memo(() => (
  <motion.section
    className="bg-white/80 backdrop-blur-lg p-6 rounded-2xl shadow-lg text-center"
    initial="hidden"
    animate="visible"
    variants={fadeInUp}
  >
    <motion.img
      src={scholarshipImg}
      alt="drOcto Scholarship"
      loading="lazy"
      className="w-full max-w-md mx-auto rounded-lg shadow-md"
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.3 }}
    />
  </motion.section>
));

ScholarshipSection.displayName = 'ScholarshipSection';

interface CTASectionProps {
  onClick: () => void;
}

const CTASection = memo(({ onClick }: CTASectionProps) => (
  <motion.section
    className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6 rounded-2xl shadow-lg text-center text-white"
    initial="hidden"
    animate="visible"
    variants={fadeInUp}
  >
    <h2 className="text-2xl md:text-3xl font-bold mb-4">{translations.en.cta.title}</h2>
    <p className="text-base md:text-lg mb-6 max-w-2xl mx-auto">{translations.en.cta.text}</p>
    <motion.button
      className="bg-white text-blue-600 px-6 py-3 rounded-full font-semibold text-base shadow-md hover:bg-blue-100"
      onClick={onClick}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      aria-label="Join"
    >
      {translations.en.cta.button}
    </motion.button>
  </motion.section>
));

CTASection.displayName = 'CTASection';
CTASection.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const Footer = memo(() => (
  <footer className="bg-gray-900 text-white py-12 px-4">
    <div className="max-w-7xl mx-auto text-center">
      <img
        src={footerImage}
        alt="drOcto Footer Logo"
        loading="lazy"
        className="mx-auto mb-6 max-w-[200px]"
      />
      <p className="text-base md:text-lg mb-4">{translations.en.footer.tagline}</p>
      <p className="text-sm">© {new Date().getFullYear()} drOcto. All rights reserved.</p>
    </div>
  </footer>
));

Footer.displayName = 'Footer';

// Main combined component
const Schools: React.FC = () => {
  const [showDashboard, setShowDashboard] = useState(false);

  const initialFormData = {
    phoneNumber: '',
    password: '',
    schoolCode: '',
    schoolName: '',
    city: '',
    pincode: '',
    schoolImage: '' as string | File,
  };

  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState(initialFormData);
  const [school, setSchool] = useState<School | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [error, setError] = useState<string[]>([]);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Scroll to top on initial mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleCtaClick = useCallback(() => {
    setShowDashboard(true);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError(['Please upload a valid image file']);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError(['Image size must be less than 5MB']);
        return;
      }
      setFormData((prev) => ({ ...prev, schoolImage: file }));
      setImagePreview(URL.createObjectURL(file));
      setError([]);
    }
  };

  const handleImageClick = () => fileInputRef.current?.click();

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, schoolImage: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
    setError([]);
  };

  const handleRegister = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setError([]);
    setSuccess('');

    try {
      let schoolImageBase64 = null;
      if (formData.schoolImage && typeof formData.schoolImage !== 'string') {
        const reader = new FileReader();
        schoolImageBase64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(formData.schoolImage as File);
        });
      }

      const data = {
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        schoolName: formData.schoolName,
        city: formData.city,
        pincode: formData.pincode,
        schoolCode: formData.schoolCode || null,
        schoolImage: schoolImageBase64 || null,
      };

      const response = await axios.post('/api/schools/register', data, {
        headers: { 'Content-Type': 'application/json' },
      });
      setSuccess('Registration successful! Please log in.');
      setFormData(initialFormData);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      if (err.response?.data?.errors) {
        setError(err.response.data.errors.map((e: any) => e.msg));
      } else {
        setError([err.response?.data?.message || 'Registration failed.']);
      }
    }
  };

  const handleLogin = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setError([]);
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post('/api/schools/login', {
        phoneNumber: formData.phoneNumber,
        password: formData.password,
      });
      const { token, school } = response.data;
      setSchool(school);
      localStorage.setItem('token', token);
      setSuccess('');

      const studentsResponse = await axios.get(`/api/schools/${school._id}/students`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(studentsResponse.data.students);
    } catch (err: any) {
      setError([err.response?.data?.message || 'Login failed.']);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setSchool(null);
    setStudents([]);
    setSearchQuery('');
    setShowSuggestions(false);
    localStorage.removeItem('token');
    setSuccess('Logged out successfully.');
    setFormData(initialFormData);
    setImagePreview(null);
    setShowDashboard(false);
  };

  const toggleForm = () => {
    setIsLogin(!isLogin);
    setError([]);
    setSuccess('');
    setFormData(initialFormData);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowSuggestions(query.length > 0);
  };

  const handleSuggestionClick = (fullName: string) => {
    setSearchQuery(fullName);
    setShowSuggestions(false);
  };

  const filteredStudents = students.filter((student) =>
    student.fullName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const suggestions = students
    .filter((student) => student.fullName.toLowerCase().includes(searchQuery.toLowerCase()))
    .slice(0, 5);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (showDashboard) return;
    const container = document.getElementById('particles');
    if (container) {
      const particleCount = 50;
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        const size = Math.random() * 4 + 2;
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        const posX = Math.random() * 100;
        const posY = Math.random() * 100;
        particle.style.left = `${posX}%`;
        particle.style.top = `${posY}%`;
        container.appendChild(particle);

        setInterval(() => {
          const newPosX = parseFloat(particle.style.left) + (Math.random() * 2 - 1) * 0.1;
          const newPosY = parseFloat(particle.style.top) + (Math.random() * 2 - 1) * 0.1;
          particle.style.left = `${newPosX}%`;
          particle.style.top = `${newPosY}%`;
        }, 1000);
      }
    }
  }, [showDashboard]);

  return (
    <div className="relative min-h-screen">
      <style>{`
        :root {
          --highlight-black: #000000;
          --base-white: #ffffff;
          --glass-bg: rgba(255, 255, 255, 0.3);
          --card-blur: 12px;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html, body {
          background-color: var(--base-white);
          color: var(--highlight-black);
          margin: 0;
          overflow-x: hidden;
          scroll-behavior: smooth;
        }

        .landing-container {
          font-family: 'Poppins', sans-serif;
          background-color: #f7fafc;
        }

        .landing-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 50;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(8px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          padding: 16px;
        }

        .landing-main {
          padding-top: 96px;
          padding-bottom: 64px;
          padding-left: 16px;
          padding-right: 16px;
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 48px;
        }

        .particles-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: -1;
          overflow: hidden;
        }

        .particle {
          position: absolute;
          background: var(--highlight-black);
          border-radius: 50%;
          opacity: 0.6;
          filter: blur(2px);
        }

        .dashboard-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 100vh;
          text-align: center;
          gap: 16px;
          font-family: 'Inter', sans-serif;
        }

        .agent-card {
          position: relative;
          background: transparent;
          border-radius: 15px;
          border: 1px solid rgba(0, 0, 0, 0.1);
          padding: 24px;
          transition: all 0.3s ease;
          overflow: hidden;
          word-wrap: break-word;
          width: 100%;
          max-width: 100%;
          will-change: transform;
        }

        .agent-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: var(--glass-bg);
          backdrop-filter: blur(var(--card-blur));
          z-index: -1;
          border-radius: 15px;
        }

        .agent-card:hover {
          box-shadow: 0 0 15px var(--highlight-black);
          border-color: var(--highlight-black);
        }

        .agent-card:active {
          box-shadow: 0 0 25px var(--highlight-black);
          border-color: var(--highlight-black);
        }

        .agent-card * {
          position: relative;
          z-index: 1;
        }

        .dashboard-container h2 {
          font-size: 2rem;
          font-weight: bold;
          color: var(--highlight-black);
          margin-bottom: 16px;
        }

        .dashboard-container h3 {
          font-size: 1.5rem;
          font-weight: bold;
          color: var(--highlight-black);
          margin-top: 24px;
          margin-bottom: 16px;
        }

        .dashboard-container input {
          width: 100%;
          padding: 12px;
          background: rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          color: var(--highlight-black);
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .dashboard-container input:focus {
          outline: none;
          border-color: var(--highlight-black);
          box-shadow: 0 0 10px var(--highlight-black);
        }

        .image-upload-container {
          width: 100%;
          aspect-ratio: 1/1;
          background: rgba(0, 0, 0, 0.1);
          border: 2px dashed rgba(0, 0, 0, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .image-upload-container:hover {
          border-color: var(--highlight-black);
        }

        .image-upload-container img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 12px;
        }

        .image-upload-text {
          text-align: center;
          color: var(--highlight-black);
          padding: 16px;
        }

        .image-upload-text svg {
          margin: 0 auto 8px;
        }

        .image-upload-text p {
          color: rgba(0, 0, 0, 0.6);
        }

        .remove-image-button {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(0, 0, 0, 0.2);
          padding: 4px;
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .image-upload-container:hover .remove-image-button {
          opacity: 1;
        }

        .remove-image-button:hover {
          background: var(--highlight-black);
        }

        .dashboard-container .button {
          width: 100%;
          padding: 12px;
          border-radius: 8px;
          border: none;
          background: var(--highlight-black);
          color: var(--base-white);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .dashboard-container .button:hover {
          transform: scale(1.05);
          box-shadow: 0 0 15px var(--highlight-black);
        }

        .dashboard-container .button:active {
          transform: scale(0.95);
        }

        .logout-button {
          background: var(--highlight-black);
        }

        .toggle-button {
          background: none;
          border: none;
          color: var(--highlight-black);
          font-size: 14px;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .toggle-button:hover {
          color: var(--highlight-black);
        }

        .error {
          color: var(--highlight-black);
          font-size: 12px;
          margin-bottom: 16px;
        }

        .success {
          color: var(--highlight-black);
          font-size: 12px;
          margin-bottom: 16px;
        }

        .school-image {
          width: 100px;
          height: 100px;
          object-fit: cover;
          border-radius: 12px;
          border: 1px solid rgba(0, 0, 0, 0.2);
          flex-shrink: 0;
        }

        .school-details-card {
          background: var(--glass-bg);
          backdrop-filter: blur(var(--card-blur));
          border: 2px solid var(--highlight-black);
          border-radius: 12px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 16px;
          transition: all 0.3s ease;
          min-height: 100px;
        }

        .school-details-card:hover {
          box-shadow: 0 0 15px var(--highlight-black);
        }

        .school-details-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
          color: var(--highlight-black);
          font-size: 12px;
          text-align: left;
          min-width: 0;
        }

        .school-details-text p {
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .search-container {
          position: relative;
        }

        .suggestions {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: var(--glass-bg);
          backdrop-filter: blur(var(--card-blur));
          border: 1px solid rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 10;
          margin-top: 4px;
        }

        .suggestion-item {
          padding: 12px;
          color: var(--highlight-black);
          cursor: pointer;
          transition: background 0.3s ease;
        }

        .suggestion-item:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .student-list {
          list-style: none;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .student-item {
          display: flex;
          justify-content: space-between;
          padding: 12px;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 8px;
          border: 1px solid rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .student-item:hover {
          border-color: var(--highlight-black);
          box-shadow: 0 0 10px var(--highlight-black);
        }

        .no-students {
          color: rgba(0, 0, 0, 0.6);
          font-size: 14px;
        }

        .lottie-container {
          width: 120px;
          height: 120px;
          margin: 0 auto 16px;
        }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }

        @media (max-width: 768px) {
          .dashboard-container {
            max-width: 448px;
            padding: 12px;
          }
          .agent-card {
            padding: 16px;
            max-width: 400px;
          }
          .dashboard-container h2 {
            font-size: 1.75rem;
          }
          .dashboard-container h3 {
            font-size: 1.25rem;
          }
          .dashboard-container input, .dashboard-container .button {
            padding: 10px;
            font-size: 12px;
          }
          .school-image {
            width: 80px;
            height: 80px;
          }
          .school-details-card {
            flex-wrap: wrap;
            justify-content: center;
          }
          .school-details-text {
            flex: 1;
            margin-left: 16px;
          }
          .school-details-text p {
            white-space: normal;
            overflow: visible;
            text-overflow: clip;
          }
          .lottie-container {
            width: 100px;
            height: 100px;
          }
        }
      `}</style>

      <AnimatePresence mode="wait">
        {!showDashboard ? (
          <motion.div
            key="landing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="landing-container min-h-screen"
          >
            <div className="particles-container" id="particles"></div>
            <motion.header
              className="landing-header"
              initial={{ y: -100 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="max-w-7xl mx-auto flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Lottie
                    animationData={octopusAnimation}
                    loop={3}
                    className="w-12 h-12 sm:w-12 sm:h-12"
                    aria-label="drOcto Logo Animation"
                  />
                  <span className="text-xl sm:text-2xl font-bold text-gray-800">{translations.en.header.logo}</span>
                </div>
                <motion.button
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-2 rounded-full font-semibold text-sm sm:text-base shadow-md hover:shadow-lg"
                  onClick={handleCtaClick}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Join Now"
                >
                  {translations.en.header.contact}
                </motion.button>
              </div>
            </motion.header>

            <main className="landing-main">
              <motion.section className="text-center" initial="hidden" animate="visible" variants={fadeInUp}>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-800 mb-4 leading-tight">
                  {translations.en.main.title}
                </h1>
                <p className="text-lg sm:text-xl text-gray-600 mb-6 max-w-3xl mx-auto">{translations.en.main.subtitle}</p>
                <TrustBadges />
              </motion.section>

              <WhatIsDrOcto />
              <WhyCollaborate />
              <GameSection />
              <ScholarshipSection />
              <TrainedOnSection />
              <CTASection onClick={handleCtaClick} />
            </main>

            <Footer />
          </motion.div>
        ) : (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="dashboard-container min-h-screen"
          >
            <motion.div
              className="agent-card animate-fade-in"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="lottie-container">
                <Lottie animationData={octopusAnimation} loop={true} />
              </div>
              <h2>{isLogin ? 'School Dashboard' : 'Create School Account'}</h2>

              <AnimatePresence>
                {error.length > 0 && (
                  <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="error">
                    {error.map((err, index) => (
                      <li key={index}>{err}</li>
                    ))}
                  </motion.ul>
                )}
                {success && (
                  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="success">
                    {success}
                  </motion.p>
                )}
              </AnimatePresence>

              {!school ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="input"
                    required
                  />
                  <input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="input"
                    required
                  />
                  {!isLogin && (
                    <>
                      <input
                        type="text"
                        name="schoolName"
                        placeholder="School Name"
                        value={formData.schoolName}
                        onChange={handleInputChange}
                        className="input"
                        required
                      />
                      <input
                        type="text"
                        name="city"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="input"
                        required
                      />
                      <input
                        type="text"
                        name="pincode"
                        placeholder="Pincode"
                        value={formData.pincode}
                        onChange={handleInputChange}
                        className="input"
                        required
                      />
                      <div>
                        <div className="image-upload-container group" onClick={handleImageClick}>
                          {imagePreview ? (
                            <>
                              <img src={imagePreview} alt="Preview" />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveImage();
                                }}
                                className="remove-image-button"
                              >
                                <X className="h-4 w-4 text-white" />
                              </button>
                            </>
                          ) : (
                            <div className="image-upload-text">
                              <Upload className="h-8 w-8 group-hover:scale-110 transition-transform" />
                              <span className="text-sm font-medium">Upload School Logo</span>
                              <p className="text-xs mt-1">Max 5MB</p>
                            </div>
                          )}
                          <input
                            type="file"
                            name="schoolImage"
                            accept="image/*"
                            ref={fileInputRef}
                            className="hidden"
                            onChange={handleImageUpload}
                          />
                        </div>
                        {error.includes('Please upload a valid image file') ||
                        error.includes('Image size must be less than 5MB') ? (
                          <p className="text-sm text-[var(--highlight-black)] mt-1">
                            {error.find((err) => err.includes('image'))}
                          </p>
                        ) : null}
                      </div>
                    </>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={isLogin ? handleLogin : handleRegister}
                    className="button"
                  >
                    {isLogin ? 'Login' : 'Register'}
                  </motion.button>
                  <p className="text-center text-gray-400">
                    {isLogin ? "Don't have an account?" : 'Already have an account?'}
                    <button onClick={toggleForm} className="toggle-button">
                      {isLogin ? 'Register' : 'Login'}
                    </button>
                  </p>
                </div>
              ) : (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="space-y-6">
                  <div className="flex justify-end">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleLogout}
                      className="button logout-button"
                    >
                      Logout
                    </motion.button>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="school-details-card"
                  >
                    {school.schoolImage && (
                      <motion.img
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        src={school.schoolImage}
                        alt={school.schoolName}
                        className="school-image"
                      />
                    )}
                    <div className="school-details-text">
                      <p>
                        <strong>{school.schoolName}</strong>
                      </p>
                      <p>
                        <strong>
                          {school.city}, {school.pincode}
                        </strong>
                      </p>
                      <p>
                        <strong>School Code:</strong> {school.schoolCode || 'Not assigned'}
                      </p>
                      <p>
                        <strong>Students:</strong> {students.length}
                      </p>
                    </div>
                  </motion.div>

                  <h3>Connected Students</h3>
                  {loading ? (
                    <p className="no-students">Loading students...</p>
                  ) : students.length > 0 ? (
                    <div className="space-y-4">
                      <div className="search-container" ref={searchRef}>
                        <input
                          type="text"
                          placeholder="Search students by name..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          className="input"
                        />
                        <AnimatePresence>
                          {showSuggestions && suggestions.length > 0 && (
                            <motion.ul
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="suggestions"
                            >
                              {suggestions.map((student) => (
                                <motion.li
                                  key={student._id}
                                  whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
                                  className="suggestion-item"
                                  onClick={() => handleSuggestionClick(student.fullName)}
                                >
                                  {student.fullName}
                                </motion.li>
                              ))}
                            </motion.ul>
                          )}
                        </AnimatePresence>
                      </div>
                      <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="student-list">
                        {filteredStudents.length > 0 ? (
                          filteredStudents.map((student) => (
                            <motion.li
                              key={student._id}
                              initial={{ x: -20, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              className="student-item"
                            >
                              <span>{student.fullName}</span>
                              <span>{student.points} Points</span>
                            </motion.li>
                          ))
                        ) : (
                          <p className="no-students">No students match your search.</p>
                        )}
                      </motion.ul>
                    </div>
                  ) : (
                    <p className="no-students">No students connected to this school.</p>
                  )}
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Schools;